import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Divider,
  Avatar,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Pagination,
  Stack,
  Grid,
  Chip,
  LinearProgress,
  Rating,
  Button,
  Tabs,
  Tab,
  Tooltip as MuiTooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ForumIcon from "@mui/icons-material/Forum";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import StarIcon from "@mui/icons-material/Star";
import LabelIcon from "@mui/icons-material/Label";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import TimelineIcon from "@mui/icons-material/Timeline";
import CategoryIcon from "@mui/icons-material/Category";
import SchoolIcon from "@mui/icons-material/School";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PersonIcon from "@mui/icons-material/Person";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from "../../utils/permissionUtils";
import { formatDistanceToNow, parseISO } from "date-fns";

const PERMISSION_SLUG = "chat-analysis";
const SENTIMENT_COLORS = {
  positive: "#10b981",
  negative: "#ef4444",
  neutral: "#9ca3af",
};
const INTENT_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#06b6d4", "#ec4899"];

const SummaryCard = ({ icon, label, value, sublabel, color }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ color: color || "primary.main", display: "flex" }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" fontWeight={700} color="text.primary">
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="caption" color="text.secondary">
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SentimentBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" color="text.primary" sx={{ textTransform: "capitalize" }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {count} ({pct}%)
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: "action.hover",
          "& .MuiLinearProgress-bar": { bgcolor: color },
        }}
      />
    </Box>
  );
};

const ConversationCard = ({ item, accent }) => {
  if (!item) return null;
  const score = Number(item.sentiment_score || 0).toFixed(2);
  return (
    <Card variant="outlined" sx={{ mb: 1, borderLeft: `4px solid ${accent}` }}>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, gap: 1 }}>
          <Chip
            label={item.sentiment || "—"}
            size="small"
            sx={{ bgcolor: accent, color: "#fff", textTransform: "capitalize" }}
          />
          {item.intent && <Chip label={item.intent} size="small" variant="outlined" />}
          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
            score {score}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          color="text.primary"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.text}
        </Typography>
      </CardContent>
    </Card>
  );
};

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

export default function ChatAnalysis() {
  const theme = useTheme();
  const { chatbotId: paramsChatbotId } = useParams();

  const [sessions, setSessions] = useState({});
  const [totals, setTotals] = useState({
    total_sessions: 0,
    total_messages: 0,
    trained_messages: 0,
    untrained_messages: 0,
    avg_messages_per_session: 0,
  });
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [intentDistribution, setIntentDistribution] = useState([]);
  const [topPositive, setTopPositive] = useState([]);
  const [topNegative, setTopNegative] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [topQuestions, setTopQuestions] = useState([]);
  const [responseStats, setResponseStats] = useState(null);
  const [feedbackSummary, setFeedbackSummary] = useState(null);

  const [tab, setTab] = useState(0);
  const [activeChat, setActiveChat] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [chatsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [canView, setCanView] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission(PERMISSION_SLUG, "view");
        setCanView(viewPerm);
      } catch (e) {
        console.error("Error checking permissions:", e);
        setCanView(false);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    document.title = "Chat Analysis | KlonIT Web Application";
    if (canView && !checkingPermissions) {
      fetchAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, checkingPermissions]);

  const fetchAnalysis = async ({ isRefresh = false } = {}) => {
    try {
      // On refresh keep the page visible; only blank it on the first load.
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-chat-analysis`, { params });
      const data =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;
      setSessions(data.sessions || {});
      setTotals(
        data.totals || {
          total_sessions: 0,
          total_messages: 0,
          trained_messages: 0,
          untrained_messages: 0,
          avg_messages_per_session: 0,
        }
      );
      setActivityTimeline(data.activity_timeline || []);
      setIntentDistribution(data.intent_distribution || []);
      setTopPositive(data.top_positive_conversations || []);
      setTopNegative(data.top_negative_conversations || []);
      setAnalysis(data.analysis || null);
      setTopQuestions(data.top_questions || []);
      setResponseStats(data.response_stats || null);
      setFeedbackSummary(data.feedback_summary || null);
      setError(null);
    } catch (err) {
      console.error("Error fetching chat analysis:", err);
      if (err.response?.status === 401) {
        setError("Authentication error: " + err.response.data.message);
      } else {
        setError("Error fetching data: " + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const runAnalysis = async () => {
    if (!paramsChatbotId) return;
    try {
      setAnalyzing(true);
      setError(null);
      setSuccessMsg(null);
      const response = await axiosInstance.post(`/trigger-chat-analysis`, {
        chatbot_id: paramsChatbotId,
      });
      setSuccessMsg(
        response.data.message ||
          `Analysis completed. ${response.data.total_analyzed || 0} conversations analyzed.`
      );
      await fetchAnalysis();
    } catch (err) {
      console.error("Error running analysis:", err);
      setError("Error running analysis: " + (err.response?.data?.message || err.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const formatRelativeDate = (dateString) => {
    if (dateString === "N/A" || !dateString) return "Invalid date";
    const date = parseISO(dateString);
    if (isNaN(date)) return "Invalid date";
    return formatDistanceToNow(date, { addSuffix: true }).replace("about ", "");
  };

  const sortedChatIds = useMemo(
    () =>
      Object.keys(sessions).sort((a, b) => {
        const dateA = sessions[a][0]?.created_date
          ? parseISO(sessions[a][0].created_date)
          : new Date(0);
        const dateB = sessions[b][0]?.created_date
          ? parseISO(sessions[b][0].created_date)
          : new Date(0);
        return dateB - dateA;
      }),
    [sessions]
  );

  const totalChats = sortedChatIds.length;
  const totalPages = Math.ceil(totalChats / chatsPerPage);
  const indexOfLastChat = currentPage * chatsPerPage;
  const indexOfFirstChat = indexOfLastChat - chatsPerPage;
  const currentChats = sortedChatIds.slice(indexOfFirstChat, indexOfLastChat);

  const sentimentTotal = useMemo(() => {
    const sb = analysis?.sentiment_breakdown || {};
    return (sb.positive || 0) + (sb.negative || 0) + (sb.neutral || 0);
  }, [analysis]);

  const sentimentPieData = useMemo(() => {
    const sb = analysis?.sentiment_breakdown || {};
    return [
      { name: "Positive", value: sb.positive || 0, color: SENTIMENT_COLORS.positive },
      { name: "Neutral", value: sb.neutral || 0, color: SENTIMENT_COLORS.neutral },
      { name: "Negative", value: sb.negative || 0, color: SENTIMENT_COLORS.negative },
    ].filter((d) => d.value > 0);
  }, [analysis]);

  const trainedPct = useMemo(() => {
    if (!totals.total_messages) return 0;
    return Math.round((totals.trained_messages / totals.total_messages) * 100);
  }, [totals]);

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">
          <strong>Access Denied</strong>
          <br />
          You don't have permission to view chat analysis. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 5, mt: 10 }}>
        <CircularProgress size={30} />
        <Typography ml={2}>Loading chat analysis...</Typography>
      </Box>
    );
  }

  const hasAnalysis = !!analysis && (analysis.response_count || 0) > 0;
  const sentimentBreakdown = analysis?.sentiment_breakdown || {};

  return (
    <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1400px", bgcolor: "background.paper" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" color="textPrimary">
            Chat <strong>Analysis</strong>
          </Typography>
          {analysis?.analyzed_at && (
            <Typography variant="caption" color="text.secondary">
              Last analyzed {formatRelativeDate(analysis.analyzed_at)}
            </Typography>
          )}
        </Box>
        <MuiTooltip
          title={
            totals.total_messages === 0
              ? "No chat history available to analyze"
              : "Run AI analysis on the latest conversations"
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              onClick={runAnalysis}
              disabled={analyzing || totals.total_messages === 0}
            >
              {analyzing ? "Analyzing..." : hasAnalysis ? "Re-analyze" : "Analyze Now"}
            </Button>
          </span>
        </MuiTooltip>
      </Box>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Sessions" icon={<ForumIcon />} iconPosition="start" />
        <Tab label="Deep Analysis" icon={<TimelineIcon />} iconPosition="start" />
      </Tabs>

      {/* ─── Sessions Tab ─── */}
      <TabPanel value={tab} index={0}>
        {/* Refresh bar — always visible so new chats/messages can be pulled in */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {totals.total_sessions} session{totals.total_sessions === 1 ? "" : "s"} ·{" "}
            {totals.total_messages} message{totals.total_messages === 1 ? "" : "s"}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />
            }
            onClick={() => fetchAnalysis({ isRefresh: true })}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>

        {currentChats.length > 0 ? (
          <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
            <Paper sx={{ flex: "0 0 350px", p: 2, bgcolor: "background.default" }}>
              <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                Sessions
              </Typography>
              <List sx={{ p: 0 }}>
                {currentChats.map((chatId) => (
                  <ListItem
                    key={chatId}
                    disablePadding
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: activeChat === chatId ? "action.selected" : "transparent",
                    }}
                  >
                    <ListItemButton
                      onClick={() => setActiveChat(chatId)}
                      sx={{ borderRadius: 1, "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <ListItemAvatar>
                        <Avatar src="/images/avatars/avatar.jpg" alt="avatar" />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600} noWrap>
                            {chatId}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeDate(sessions[chatId][0]?.created_date)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, v) => setCurrentPage(v)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Paper>

            <Box sx={{ flex: 1 }}>
              {activeChat ? (
                <Paper sx={{ p: 3, bgcolor: "background.default", minHeight: "500px" }}>
                  <Typography variant="h6" sx={{ mb: 3 }} color="text.primary">
                    Conversation
                  </Typography>
                  <Stack spacing={2}>
                    {sessions[activeChat]
                      .slice()
                      .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
                      .map((message, index) => (
                        <React.Fragment key={index}>
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                            <Card
                              sx={{
                                maxWidth: "70%",
                                bgcolor:
                                  theme.palette.mode === "dark" ? "#1e293b" : "#e3f2fd",
                              }}
                            >
                              <CardContent>
                                <Typography variant="body1">{message.questions}</Typography>
                              </CardContent>
                            </Card>
                            <Avatar src="/images/avatars/avatar.jpg" alt="user" />
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
                            <Avatar src="/images/avatars/avatar_img (1).png" alt="bot" />
                            <Card sx={{ maxWidth: "70%", bgcolor: "background.paper" }}>
                              <CardContent>
                                <Typography variant="body1">{message.answers}</Typography>
                              </CardContent>
                            </Card>
                          </Box>
                        </React.Fragment>
                      ))}
                  </Stack>
                </Paper>
              ) : (
                <Paper
                  sx={{
                    p: 5,
                    bgcolor: "background.default",
                    minHeight: "500px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box textAlign="center">
                    <ForumIcon sx={{ fontSize: 48, color: "text.disabled" }} />
                    <Typography mt={2} color="text.secondary">
                      Select a session to view conversation
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Box>
          </Box>
        ) : (
          <Box textAlign="center" py={8}>
            <ForumIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography mt={2} variant="h6" color="text.primary">
              No Chat Sessions Found
            </Typography>
            <Typography color="text.secondary">
              Start a conversation to see chat history here
            </Typography>
          </Box>
        )}
      </TabPanel>

      {/* ─── Chat Analysis Tab ─── */}
      <TabPanel value={tab} index={1}>
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              icon={<ForumIcon />}
              label="Total Sessions"
              value={totals.total_sessions}
              sublabel={`${totals.avg_messages_per_session} msgs/session avg`}
              color="#3b82f6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              icon={<ChatBubbleOutlineIcon />}
              label="Total Messages"
              value={totals.total_messages}
              sublabel="Q&A exchanges"
              color="#8b5cf6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              icon={<SentimentSatisfiedAltIcon />}
              label="Avg Sentiment"
              value={hasAnalysis ? analysis.avg_sentiment_score.toFixed(2) : "—"}
              sublabel="Range: -1 to 1"
              color={
                hasAnalysis && analysis.avg_sentiment_score >= 0
                  ? SENTIMENT_COLORS.positive
                  : SENTIMENT_COLORS.negative
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <StarIcon sx={{ color: "#f59e0b" }} />
                  <Typography variant="body2" color="text.secondary">
                    Satisfaction Rating
                  </Typography>
                </Box>
                {hasAnalysis && analysis.star_rating != null ? (
                  <>
                    <Rating
                      value={Number(analysis.star_rating)}
                      precision={0.1}
                      readOnly
                      sx={{ mt: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {Number(analysis.star_rating).toFixed(1)} / 5
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h4" fontWeight={700} color="text.primary">
                    —
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Activity Timeline — always shown (works with or without AI analysis) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
              <TimelineIcon sx={{ verticalAlign: "middle", mr: 1, fontSize: 20 }} />
              Message Activity (Last 30 Days)
            </Typography>
            <Box sx={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={activityTimeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  />
                  <ReTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <Bar dataKey="messages" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ display: "flex", gap: 3, mt: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SchoolIcon sx={{ color: SENTIMENT_COLORS.positive, fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  Trained: <strong>{totals.trained_messages}</strong> ({trainedPct}%)
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SchoolIcon sx={{ color: "text.disabled", fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  Untrained: <strong>{totals.untrained_messages}</strong> (
                  {100 - trainedPct}%)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* ─── Response Stats + Feedback Summary ─── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {responseStats && responseStats.total_responses > 0 && (
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                    <TextFieldsIcon sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: "#8b5cf6" }} />
                    Average Response
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Characters
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {responseStats.avg_response_length_chars}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Words
                      </Typography>
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {responseStats.avg_response_length_words}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Shortest
                      </Typography>
                      <Typography variant="body1" color="text.primary">
                        {responseStats.min_response_length_chars} chars
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Longest
                      </Typography>
                      <Typography variant="body1" color="text.primary">
                        {responseStats.max_response_length_chars} chars
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {feedbackSummary && (
            <Grid item xs={12} md={responseStats && responseStats.total_responses > 0 ? 6 : 12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                    Happy vs Bad
                    <Chip
                      size="small"
                      label={
                        feedbackSummary.user_feedback.with_feedback > 0 && feedbackSummary.ai_inferred.analyzed > 0
                          ? "user + AI"
                          : feedbackSummary.user_feedback.with_feedback > 0
                          ? "user feedback"
                          : "AI inferred"
                      }
                      sx={{ ml: 1, fontSize: "0.7rem" }}
                    />
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                      <ThumbUpIcon sx={{ color: SENTIMENT_COLORS.positive, fontSize: 28 }} />
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {feedbackSummary.combined.happy}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Happy
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                      <ThumbDownIcon sx={{ color: SENTIMENT_COLORS.negative, fontSize: 28 }} />
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {feedbackSummary.combined.bad}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Bad
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: "center" }}>
                      <SentimentNeutralIcon sx={{ color: SENTIMENT_COLORS.neutral, fontSize: 28 }} />
                      <Typography variant="h5" fontWeight={700} color="text.primary">
                        {feedbackSummary.combined.neutral}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Neutral
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    <PersonIcon sx={{ verticalAlign: "middle", fontSize: 14, mr: 0.5 }} />
                    User feedback: <strong>{feedbackSummary.user_feedback.with_feedback}</strong> of{" "}
                    {feedbackSummary.total_messages}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    <SmartToyIcon sx={{ verticalAlign: "middle", fontSize: 14, mr: 0.5 }} />
                    AI inferred: <strong>{feedbackSummary.ai_inferred.analyzed}</strong> conversations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {feedbackSummary &&
            (feedbackSummary.happy_examples.length > 0 || feedbackSummary.bad_examples.length > 0) && (
              <>
                {feedbackSummary.happy_examples.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1.5 }} color="text.primary">
                          <ThumbUpIcon
                            sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: SENTIMENT_COLORS.positive }}
                          />
                          Happy Examples
                        </Typography>
                        <Stack spacing={1}>
                          {feedbackSummary.happy_examples.map((ex, i) => (
                            <Box
                              key={i}
                              sx={{ borderLeft: `3px solid ${SENTIMENT_COLORS.positive}`, pl: 1.5 }}
                            >
                              <Chip
                                label={ex.source === "user" ? "User" : "AI"}
                                size="small"
                                icon={ex.source === "user" ? <PersonIcon /> : <SmartToyIcon />}
                                sx={{ mb: 0.5, fontSize: "0.65rem", height: 20 }}
                              />
                              {ex.question && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Q: {ex.question}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.primary">
                                {ex.comment}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {feedbackSummary.bad_examples.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1.5 }} color="text.primary">
                          <ThumbDownIcon
                            sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: SENTIMENT_COLORS.negative }}
                          />
                          Bad Examples
                        </Typography>
                        <Stack spacing={1}>
                          {feedbackSummary.bad_examples.map((ex, i) => (
                            <Box
                              key={i}
                              sx={{ borderLeft: `3px solid ${SENTIMENT_COLORS.negative}`, pl: 1.5 }}
                            >
                              <Chip
                                label={ex.source === "user" ? "User" : "AI"}
                                size="small"
                                icon={ex.source === "user" ? <PersonIcon /> : <SmartToyIcon />}
                                sx={{ mb: 0.5, fontSize: "0.65rem", height: 20 }}
                              />
                              {ex.question && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Q: {ex.question}
                                </Typography>
                              )}
                              <Typography variant="body2" color="text.primary">
                                {ex.comment}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </>
            )}
        </Grid>

        {/* ─── Top Questions ─── */}
        {topQuestions.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                <QuestionAnswerIcon
                  sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: "#3b82f6" }}
                />
                Top Questions
                <Chip
                  label={`${topQuestions.length} unique`}
                  size="small"
                  sx={{ ml: 1, fontSize: "0.7rem" }}
                />
              </Typography>
              <Stack spacing={1}>
                {topQuestions.map((q, i) => {
                  const trainedPct = q.count > 0 ? Math.round((q.trained_count / q.count) * 100) : 0;
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Chip
                        label={`#${i + 1}`}
                        size="small"
                        sx={{ minWidth: 36, fontWeight: 600 }}
                        color="primary"
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {q.question}
                        </Typography>
                        {q.last_asked && (
                          <Typography variant="caption" color="text.secondary">
                            Last asked {formatRelativeDate(q.last_asked)}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ textAlign: "right", minWidth: 80 }}>
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          {q.count}×
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {trainedPct}% trained
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}

        {!hasAnalysis ? (
          <Alert severity="info">
            No AI analysis yet for this chatbot.{" "}
            {totals.total_messages > 0
              ? 'Click "Analyze Now" above to generate sentiment, keywords, themes, and intent insights.'
              : "Once you have chat history, you can run analysis here."}
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {/* Sentiment Breakdown + Pie */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                    Sentiment Breakdown
                  </Typography>
                  <SentimentBar
                    label="positive"
                    count={sentimentBreakdown.positive || 0}
                    total={sentimentTotal}
                    color={SENTIMENT_COLORS.positive}
                  />
                  <SentimentBar
                    label="neutral"
                    count={sentimentBreakdown.neutral || 0}
                    total={sentimentTotal}
                    color={SENTIMENT_COLORS.neutral}
                  />
                  <SentimentBar
                    label="negative"
                    count={sentimentBreakdown.negative || 0}
                    total={sentimentTotal}
                    color={SENTIMENT_COLORS.negative}
                  />
                  <Box sx={{ display: "flex", justifyContent: "space-around", mt: 2 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <SentimentSatisfiedAltIcon sx={{ color: SENTIMENT_COLORS.positive }} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Positive
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <SentimentNeutralIcon sx={{ color: SENTIMENT_COLORS.neutral }} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Neutral
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <SentimentDissatisfiedIcon sx={{ color: SENTIMENT_COLORS.negative }} />
                      <Typography variant="caption" display="block" color="text.secondary">
                        Negative
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Sentiment Pie */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                    Sentiment Distribution
                  </Typography>
                  {sentimentPieData.length > 0 ? (
                    <Box sx={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={sentimentPieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={85}
                            paddingAngle={2}
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                          >
                            {sentimentPieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <ReTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No sentiment data available.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Intent Distribution */}
            {intentDistribution.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                      <CategoryIcon
                        sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: "#3b82f6" }}
                      />
                      Intent Distribution
                    </Typography>
                    <Box sx={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={intentDistribution}
                          layout="vertical"
                          margin={{ top: 5, right: 20, left: 30, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis
                            type="number"
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                          />
                          <YAxis
                            type="category"
                            dataKey="intent"
                            tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                            width={110}
                          />
                          <ReTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          />
                          <Bar dataKey="count" radius={[0, 3, 3, 0]}>
                            {intentDistribution.map((_, i) => (
                              <Cell key={i} fill={INTENT_COLORS[i % INTENT_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Top Keywords */}
            <Grid item xs={12} md={intentDistribution.length > 0 ? 6 : 12}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                    <LabelIcon sx={{ verticalAlign: "middle", mr: 1, fontSize: 20 }} />
                    Top Keywords
                  </Typography>
                  {analysis.keywords && analysis.keywords.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {analysis.keywords.slice(0, 30).map((kw, i) => {
                        const word = typeof kw === "string" ? kw : kw.word;
                        const count = typeof kw === "string" ? null : kw.count;
                        const size = Math.min(1.2, 0.75 + (count ? count / 50 : 0));
                        return (
                          <Chip
                            key={`${word}-${i}`}
                            label={count ? `${word} · ${count}` : word}
                            variant="outlined"
                            sx={{ fontSize: `${size}rem` }}
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No keywords extracted.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* AI Summary */}
            {analysis.summary && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1 }} color="text.primary">
                      AI Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {analysis.summary}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Top Positive Conversations */}
            {topPositive.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                      <SentimentSatisfiedAltIcon
                        sx={{
                          verticalAlign: "middle",
                          mr: 1,
                          fontSize: 20,
                          color: SENTIMENT_COLORS.positive,
                        }}
                      />
                      Top Positive Conversations
                    </Typography>
                    {topPositive.map((item, i) => (
                      <ConversationCard
                        key={i}
                        item={item}
                        accent={SENTIMENT_COLORS.positive}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Top Negative Conversations */}
            {topNegative.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                      <SentimentDissatisfiedIcon
                        sx={{
                          verticalAlign: "middle",
                          mr: 1,
                          fontSize: 20,
                          color: SENTIMENT_COLORS.negative,
                        }}
                      />
                      Top Negative Conversations
                    </Typography>
                    {topNegative.map((item, i) => (
                      <ConversationCard
                        key={i}
                        item={item}
                        accent={SENTIMENT_COLORS.negative}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Themes */}
            {analysis.themes && analysis.themes.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                      <LightbulbIcon
                        sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: "#f59e0b" }}
                      />
                      Key Themes
                    </Typography>
                    <List dense>
                      {analysis.themes.map((theme, i) => (
                        <ListItem key={i} sx={{ pl: 0 }}>
                          <ListItemText
                            primary={typeof theme === "string" ? theme : JSON.stringify(theme)}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.primary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Urgent Issues */}
            {analysis.urgent_issues && analysis.urgent_issues.length > 0 && (
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }} color="text.primary">
                      <ReportProblemIcon
                        sx={{ verticalAlign: "middle", mr: 1, fontSize: 20, color: "#ef4444" }}
                      />
                      Urgent Issues
                    </Typography>
                    <List dense>
                      {analysis.urgent_issues.map((issue, i) => (
                        <ListItem key={i} sx={{ pl: 0 }}>
                          <ListItemText
                            primary={typeof issue === "string" ? issue : JSON.stringify(issue)}
                            primaryTypographyProps={{
                              variant: "body2",
                              color: "text.primary",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </TabPanel>
    </Paper>
  );
}
